
















































































































































































































import Vue from 'vue';
import { SpinnerState } from '@/enums/spinnerState';
import { MessageType } from '@/enums/messageType';
import { dialogueMessageService } from '@/services/dialogueMessageService';
import { toolsRepository } from '@/api';
import { ExportJsonConfig } from '@/api/interfaces/tools/exportJsonConfig';
import { ExportJsonPreviewResult } from '@/api/interfaces/tools/exportJsonPreviewResult';
import SpinnerArea from '@/components/molecules/spinnerArea/SpinnerArea.vue';
import AppButton from '@/components/atoms/appButton/AppButton.vue';
import FormTextBlock from '@/components/molecules/formTextBlock/FormTextBlock.vue';

const defaultConfig: Partial<ExportJsonConfig> = {
    entityFields: [
        'applicationId',
        'cidaasSub',
        'applicationNumber',
        'status',
        'sentDate',
        'plantType',
    ],
    dataPaths: ['location', 'plantBuilder', 'plantOperator'],
    excludePaths: [],
};

const infoText = [
    'Exports applications as NDJSON (one json object per line, first line',
    'contains export metadata and the configuration used). Only applications',
    'that are not in status Draft are exported. Filter by sent date and',
    'configure the exported fields below. Entity fields are placed at the top',
    'level of each entry, data paths select subtrees of the application data',
    '(kept DEA-compatible). Exclude paths are removed afterwards.',
].join(' ');

const hashNote = [
    'The last line of the export file contains a footer with completion',
    'metadata (applicationCount, durationSeconds) and a SHA256 hash over all',
    'preceding lines. The hash is computed over the exact bytes written by',
    'the server, including the platform-native line endings (CRLF on Windows).',
].join(' ');

const hashCheckScript = [
    '$f = ".\\dea-export.ndjson"',
    '$bytes = [IO.File]::ReadAllBytes($f)',
    '$text = [Text.Encoding]::UTF8.GetString($bytes)',
    '$lastLf = $text.LastIndexOf("`n", $text.Length - 2)',
    '$content = $text.Substring(0, $lastLf + 1)',
    '$contentBytes = [Text.Encoding]::UTF8.GetBytes($content)',
    '$footer = $text.Substring($lastLf + 1).Trim() | ConvertFrom-Json',
    '$hash = [BitConverter]::ToString(',
    '    [Security.Cryptography.SHA256]::Create().ComputeHash($contentBytes)',
    ').Replace("-", "").ToLower()',
    '$hash -eq $footer.exportMeta.sha256',
].join('\n');

interface ExportHeaderMeta {
    exportDate?: string;
    sentFrom?: string | null;
    sentTo?: string | null;
    config?: Partial<ExportJsonConfig>;
}

function isExportHeader(
    parsed: unknown,
): parsed is { exportMeta: ExportHeaderMeta } {
    return (
        typeof parsed === 'object' && parsed !== null && 'exportMeta' in parsed
    );
}

export default Vue.extend({
    name: 'ToolExportJson',
    components: {
        SpinnerArea,
        AppButton,
        FormTextBlock,
    },
    data: () => {
        return {
            spinner: SpinnerState.off,
            infoText,
            hashNote,
            hashCheckScript,
            sentFrom: '' as string,
            sentTo: '' as string,
            configText: JSON.stringify(defaultConfig, null, 2),
            applyMigrations: true,
            clientValidationError: '',
            validationErrors: [] as string[],
            previewResult: null as ExportJsonPreviewResult | null,
            exportResultMessage: '',
        };
    },
    computed: {
        previewPretty(): string {
            return this.previewResult?.preview
                ? JSON.stringify(this.previewResult.preview, null, 2)
                : '';
        },
        estimatedSizeFormatted(): string {
            const sizeMb = this.previewResult?.estimatedSizeMb ?? 0;
            if (sizeMb >= 0.1) {
                return `${sizeMb.toFixed(1)} MB`;
            }
            return `${(sizeMb * 1024).toFixed(1)} KB`;
        },
        lastExportDate(): string | null {
            try {
                const config = JSON.parse(this.configText) as Record<
                    string,
                    unknown
                >;
                return config.exportDate ? String(config.exportDate) : null;
            } catch {
                return null;
            }
        },
    },
    watch: {
        sentFrom() {
            this.syncDatesToConfig();
        },
        sentTo() {
            this.syncDatesToConfig();
        },
    },
    created() {
        if (this.$route.query.code === 'unauthorized') {
            dialogueMessageService.show('unauthorized', MessageType.error);
        }
    },
    async mounted() {
        this.spinner = SpinnerState.opaque;

        try {
            const dateRange = await toolsRepository.getExportJsonDateRange();
            if (dateRange.earliestSentDate) {
                this.sentFrom = dateRange.earliestSentDate.slice(0, 10);
            }
            this.sentTo = new Date().toISOString().slice(0, 10);
        } catch {
            // date prefill is a convenience; ignore failures
        }

        this.spinner = SpinnerState.off;
    },
    methods: {
        cleanMessages() {
            this.clientValidationError = '';
            this.validationErrors = [];
            this.previewResult = null;
            this.exportResultMessage = '';
        },
        resetConfig() {
            this.cleanMessages();
            this.configText = JSON.stringify(defaultConfig, null, 2);
        },
        syncDatesToConfig() {
            let config: Partial<ExportJsonConfig>;
            try {
                config = JSON.parse(this.configText);
            } catch {
                // config is not valid json; user is probably editing
                return;
            }

            config.sentFrom = this.sentFrom || null;
            config.sentTo = this.sentTo || null;
            this.configText = JSON.stringify(config, null, 2);
        },
        onConfigInput() {
            let parsed: unknown;
            try {
                parsed = JSON.parse(this.configText);
            } catch {
                // not yet valid json; user is probably still typing
                return;
            }

            // Accept a pasted first line of a previous export file and
            // extract the contained configuration into the expected format.
            if (isExportHeader(parsed)) {
                const meta = parsed.exportMeta;
                const config: Partial<ExportJsonConfig> & {
                    exportDate?: string;
                } = {
                    ...(meta.config ?? {}),
                    sentFrom: meta.sentFrom ?? null,
                    sentTo: meta.sentTo ?? null,
                };

                // Keep the export date visible/editable in the configuration;
                // it is stripped before sending to the server.
                if (meta.exportDate) {
                    config.exportDate = meta.exportDate;
                }

                this.configText = JSON.stringify(config, null, 2);
            }

            let config: Partial<ExportJsonConfig>;
            try {
                config = JSON.parse(this.configText);
            } catch {
                // not yet valid json; user is probably still typing
                return;
            }

            if (config.sentFrom) {
                this.sentFrom = String(config.sentFrom).slice(0, 10);
            }

            if (config.sentTo) {
                this.sentTo = String(config.sentTo).slice(0, 10);
            }
        },
        applyDeltaSinceLastExport() {
            const exportDate = this.lastExportDate;
            if (!exportDate) {
                return;
            }

            this.sentFrom = exportDate.slice(0, 10);
        },
        buildRequest(): ExportJsonConfig | null {
            let config: Partial<ExportJsonConfig>;
            try {
                config = JSON.parse(this.configText);
            } catch (error) {
                this.clientValidationError = `Configuration is not valid JSON: ${error}`;
                return null;
            }

            return {
                sentFrom: this.sentFrom || null,
                sentTo: this.sentTo || null,
                entityFields: config.entityFields ?? [],
                dataPaths: config.dataPaths ?? [],
                excludePaths: config.excludePaths ?? [],
                applyMigrations: this.applyMigrations,
            };
        },
        async validateAndPreview(): Promise<void> {
            this.cleanMessages();

            const request = this.buildRequest();
            if (!request) {
                return;
            }

            this.spinner = SpinnerState.transparent;

            try {
                const result = await toolsRepository.previewExportJson(request);
                this.validationErrors = result.validationErrors ?? [];
                if (this.validationErrors.length === 0) {
                    this.previewResult = result;
                }
            } catch (error) {
                this.clientValidationError = `Preview failed: ${error}`;
            }

            this.spinner = SpinnerState.off;
        },
        async exportJson(): Promise<void> {
            this.cleanMessages();

            const request = this.buildRequest();
            if (!request) {
                return;
            }

            this.spinner = SpinnerState.transparent;

            try {
                await toolsRepository.downloadExportJson(request);
                this.exportResultMessage = 'Export downloaded.';
            } catch (error) {
                const serviceError = error as {
                    messageCode?: string;
                    message?: string;
                };
                this.exportResultMessage = `Export failed: ${
                    serviceError.message || serviceError.messageCode || error
                }`;
            }

            this.spinner = SpinnerState.off;
        },
    },
});
